import { BehaviorSubject, fromEvent, Observable, Subscription } from 'rxjs';
import { debounceTime, filter, map, take } from 'rxjs/operators';

import { AfterViewInit, Component, ElementRef, Input, OnDestroy, Optional, Self, ViewChild } from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import { Router } from '@angular/router';
import { FrameworkHelper, GlobalLoaderService } from '@brafton/angular-framework-library';
import { MasterProjectsService, ProductionsService, SearchService } from '@brafton/atlas-api-client';
import { SearchResult } from '@brafton/atlas-api-client/lib/models/search.model';

@Component({
  selector: 'search-autocomplete',
  templateUrl: './search-autocomplete.component.html',
  styleUrls: ['./search-autocomplete.component.scss'],
  host: { class: 'search-autocomplete' },
})
export class SearchSearchAutocompleteComponent implements AfterViewInit, OnDestroy, ControlValueAccessor {
  @Input() placeholder?: string;
  @Input() disabled?: boolean;
  @Input() disableIcon?: boolean;
  @Input() disableSearch?: boolean;
  @Input() disableDidYouMean?: boolean;
  @Input() orgId?: number;

  autocompleteHits$?: Observable<SearchResult>;

  @ViewChild('searchInput') searchInput!: ElementRef;

  typedInput?: string;

  loading: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  // A placeholder for all subscription so we can unsubscribe all of them in the ondestroy Methods.
  subscriptions = new Subscription();

  constructor(
    @Self()
    @Optional()
    private ngControl: NgControl,
    private searchService: SearchService,
    private router: Router,
    private frameworkHelper: FrameworkHelper,
    private masterProjectsService: MasterProjectsService,
    private loaderService: GlobalLoaderService,
    private productionsService: ProductionsService,
  ) {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }
  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngAfterViewInit() {
    this.subscriptions.add(
      fromEvent(this.searchInput.nativeElement, 'keyup')
        .pipe(
          debounceTime(300),
          filter((searchTerm: any) => searchTerm.target.value.length >= 3),
          map((res: any) => {
            return res.target.value;
          }),
        )
        .subscribe((searchTerm: any) => {
          this.getHits(searchTerm);
        }),
    );
  }

  getHits(searchTerm: string) {
    this.onChange(searchTerm);
    if (this.disableSearch) return;
    this.loading.next(true);
    this.autocompleteHits$ = undefined;
    this.autocompleteHits$ = this.searchService
      .performAutocompleteSearch(searchTerm, ['person', 'organization', 'project'])
      .pipe(
        map((res: SearchResult) => {
          this.loading.next(false);
          if (!this.typedInput) return {} as SearchResult;
          return res;
        }),
      );
  }

  search() {
    if (!this.typedInput) return;
    const queryParams = { text: `${this.typedInput}:Search` };
    this.clear();
    this.router.navigate(['/search/advanced-search/search-results'], { queryParams });
  }

  getInputBoxValue(): string | undefined {
    return this.typedInput;
  }

  async selectChoice(autoCompleteChoice: any) {
    const loaderTask = this.loaderService.startGlobalTask();
    this.typedInput = autoCompleteChoice.title;
    const route = await this.getRoute(autoCompleteChoice);

    if (route) {
      this.router.navigate([route]);
    } else {
      this.search();
    }
    this.loaderService.stopTask(loaderTask);
    this.clear();
  }

  clear() {
    this.onChange(undefined);
    this.typedInput = undefined;
    this.autocompleteHits$ = undefined;
  }

  async getRoute(result: any): Promise<string | undefined> {
    if (!result?.type?.id) return undefined;
    switch (result?.type?.id) {
      case 'project': {
        const project = await this.getProjectOnId(result?.project?.id, result?.project?.module);
        // if (result?.type === 'StatementOfWork') return '/sows/' + result?.id;
        // if (result?.module === 'Campaign') return '/campaigns/' + result?.id;
        return this.frameworkHelper.findProjectRoute(project) || undefined;
      }
      case 'person': {
        if (!result?.source?.title?.toLowerCase().includes('team')) return '/profile/' + result?.user?.id;
        if (result?.source?.title?.toLowerCase().includes('team')) return '/team/' + result?.user?.id;
        return;
      }
      case 'assetfile': {
        const project = await this.getProjectOnId(result?.project?.id, result?.project?.module);
        return this.frameworkHelper.findProjectRoute(project) + '/gallery';
      }
      case 'organization':
        return '/orgs/' + result?.organization?.id;
      // case 'contract':
      //   return '/sows/' + result?.source?.source?.id;
      case 'comment': {
        const project = await this.getProjectOnId(result?.project?.id, result?.project?.module);
        return this.frameworkHelper.findProjectRoute(project) + '/messages';
      }
      default:
        return;
    }
  }

  async getProjectOnId(id: string, module: string): Promise<any> {
    // Master Project
    if (module === 'MasterProject') {
      const data = await this.masterProjectsService.getProjectDetails(id).pipe(take(1)).toPromise();
      return data;
    }

    // Internal Project - this is duplicate call the same as master projects for now but may change in the future
    if (module === 'InternalProject') {
      const data = await this.masterProjectsService.getProjectDetails(id).pipe(take(1)).toPromise();
      return data;
    }

    // Default to Production
    const data = await this.productionsService.getProductionDetails(id).pipe(take(1)).toPromise();
    return data;
  }

  getHighLightTitle(result: any): string | undefined {
    if (!result) return;
    if (result?.highlight?.title) return result?.highlight?.title;
    if (result?.source?.type?.id === 'organization') return result?.source?.source?.type;
    return result?.source?.title;
  }

  writeValue(value: any): void {
    this.typedInput = value;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  onChange(fn?: any) {}
  onTouched() {}
}
